import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ArrowLeftRight, LogOut, ShieldCheck, Clock, Check, X, Ban, Lock, CalendarClock } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ClubCrest } from "./ClubCrest";

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const STATUS_TONE = {
  AWAITING_CLUB_DECISION: "text-amber-300", AWAITING_FEDERATION_APPROVAL: "text-[#F0C72A]",
  APPROVED: "text-emerald-300", COMPLETED: "text-emerald-400",
  WAITING_90_DAY_RELEASE: "text-orange-300", ELIGIBLE_FOR_RELEASE: "text-orange-200",
  REJECTED: "text-red-400", CANCELLED: "text-slate-400",
  EXPIRED_WINDOW: "text-slate-400", CANCELLED_STATE_CHANGE: "text-slate-400",
};
const STATUS_LABEL = {
  EXPIRED_WINDOW: "Expired — transfer window closed",
  CANCELLED_STATE_CHANGE: "Cancelled — player registration changed",
};

function Shell({ title, icon, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl foil-border bg-gradient-to-b from-[#161616] to-[#070707] p-5">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#E5B842] font-mono font-bold mb-3">
        {icon} {title}
      </div>
      {children}
    </div>
  );
}

function StatusLine({ r }) {
  return (
    <div className="text-xs font-mono text-slate-400 space-y-0.5">
      <div>Status: <span className={STATUS_TONE[r.status] || "text-slate-200"} data-testid="transfer-status">{STATUS_LABEL[r.status] || r.status}</span></div>
      <div>Type: {r.request_type} · Requested: {fmt(r.requested_at)}</div>
      <div>Club consent: {r.club_consent_status} · Federation: {r.federation_status}</div>
      {(r.status === "WAITING_90_DAY_RELEASE" || r.status === "ELIGIBLE_FOR_RELEASE") && (
        <div data-testid="transfer-90day" className="text-orange-300 flex items-center gap-1">
          <Clock className="w-3 h-3" /> 90-day release eligible: {fmt(r.release_eligible_at)}
        </div>
      )}
    </div>
  );
}

export function PlayerTransferPanel({ wsbfId, player }) {
  const { user } = useAuth();
  const [sum, setSum] = useState(null);
  const [win, setWin] = useState(null);
  const [type, setType] = useState("RELEASE");
  const [target, setTarget] = useState("");
  const own = user?.player_id && player?.id && user.player_id === player.id;

  const load = useCallback(() => {
    api.get(`/transfers/player/${wsbfId}/summary`).then(({ data }) => setSum(data)).catch(() => setSum(null));
  }, [wsbfId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/transfers/window-status").then(({ data }) => setWin(data)).catch(() => setWin(null)); }, []);
  if (!sum) return null;

  const windowClosed = win && !win.is_open;
  const dfmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : null);
  const reopens = dfmt(win?.next_window?.opens);
  const closesOn = dfmt(win?.current_window?.closes);

  const create = async () => {
    if (type === "TRANSFER" && windowClosed) {
      toast.error(`Transfer window closed — club-to-club transfers reopen ${reopens || "soon"}.`);
      return;
    }
    try {
      const body = { request_type: type };
      if (type === "TRANSFER") {
        const { data } = await api.get(`/clubs/${target.trim()}`);
        body.target_club_id = data.id;
      }
      await api.post("/transfers/requests", body);
      toast.success("Transfer request submitted"); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && d.error === "TRANSFER_WINDOW_CLOSED") toast.error(d.message || "Transfer window is closed.");
      else toast.error(apiErr(e));
    }
  };
  const cancel = async () => {
    try { await api.post(`/transfers/requests/${sum.active_request.transfer_request_id}/cancel`); toast.success("Request cancelled"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="player-transfer-panel" className="rounded-2xl foil-border bg-gradient-to-b from-[#161616] to-[#070707] p-5 sm:p-6">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#E5B842] font-mono font-bold mb-4">
        <ArrowLeftRight className="w-4 h-4" /> Club &amp; Transfers
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-5 md:gap-6 items-start">
        {/* LEFT: club badge panel */}
        <div className="flex md:flex-col items-center gap-4 md:gap-3 md:w-44 lg:w-48 shrink-0 rounded-xl bg-[#0b0b0b]/70 border border-[rgba(212,175,55,0.16)] p-4 md:py-6">
          {sum.is_free_agent ? (
            <div className="flex items-center justify-center rounded-lg border border-dashed border-orange-500/40 bg-orange-950/20 shrink-0" style={{ width: 72, height: 72 }}>
              <LogOut className="w-8 h-8 text-orange-300" />
            </div>
          ) : (
            <ClubCrest club={player?.club || sum.current_club} size={72} />
          )}
          <div className="min-w-0 md:text-center">
            {sum.is_free_agent ? (
              <>
                <div data-testid="free-agent-badge" className="font-heading font-black uppercase tracking-wide text-orange-300 leading-tight">Free agent</div>
                <div className="text-[11px] font-mono text-slate-500 mt-1">Unattached</div>
              </>
            ) : (
              <>
                <div data-testid="current-club" className="font-heading font-black uppercase tracking-tight text-slate-100 leading-tight break-words">{sum.current_club?.name}</div>
                <div className="text-[11px] font-mono text-[#C8A24A] mt-1 break-all">{sum.current_club?.wsbf_club_id}</div>
              </>
            )}
          </div>
        </div>

        {/* RIGHT: window status + request / controls */}
        <div className="min-w-0 space-y-3">
          {win && (
            windowClosed ? (
              <div data-testid="transfer-window-banner" data-window-open="false"
                className="rounded-xl border border-amber-500/40 bg-amber-950/25 p-3 flex items-start gap-2.5">
                <Lock className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                <div className="text-xs">
                  <div className="font-heading font-bold uppercase tracking-wide text-amber-200">Transfer Window Closed</div>
                  <div className="text-amber-100/80 mt-0.5">Club-to-club transfers reopen <b className="text-amber-200">{reopens}</b>. Free-agent signings remain available year-round.</div>
                </div>
              </div>
            ) : (
              <div data-testid="transfer-window-banner" data-window-open="true"
                className="rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-2.5 flex items-center gap-2 text-xs">
                <CalendarClock className="w-4 h-4 text-emerald-300 shrink-0" />
                <span className="text-emerald-200"><b className="font-heading uppercase tracking-wide">Transfer Window Open</b>{closesOn ? <> · closes {closesOn}</> : null}</span>
              </div>
            )
          )}

          {sum.is_free_agent && (
            <div className="text-[11px] text-slate-500">Free agents can be signed by a club at any time, year-round (subject to roster space).</div>
          )}

          {sum.active_request ? (
            <div className="rounded-xl bg-[#070707] border border-[#2b2512] p-3">
              <StatusLine r={sum.active_request} />
              {own && (
                <button data-testid="cancel-transfer-btn" onClick={cancel}
                  className="mt-2 inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200">
                  <Ban className="w-3.5 h-3.5" /> Cancel request
                </button>
              )}
            </div>
          ) : (
            own && !sum.is_free_agent && (
              <div data-testid="create-transfer-form" className="rounded-xl bg-[#070707] border border-[#2b2512] p-3 space-y-2">
                <div className="flex gap-2 flex-wrap">
                  <select data-testid="transfer-type" value={type} onChange={(e) => setType(e.target.value)}
                    className="bg-[#161616] border border-[#2b2512] rounded px-2 py-1 text-xs text-slate-200">
                    <option value="RELEASE">Release (become free agent)</option>
                    <option value="TRANSFER" disabled={windowClosed}>Transfer to club{windowClosed ? " (window closed)" : ""}</option>
                  </select>
                  {type === "TRANSFER" && (
                    <input data-testid="transfer-target" value={target} onChange={(e) => setTarget(e.target.value)}
                      placeholder="Target WSTF-CLUB-… id" className="flex-1 min-w-[160px] bg-[#161616] border border-[#2b2512] rounded px-2 py-1 text-xs text-slate-200" />
                  )}
                </div>
                {type === "TRANSFER" && windowClosed && (
                  <div className="text-[11px] text-amber-300/90 flex items-center gap-1.5"><Lock className="w-3 h-3" /> Club-to-club transfers are closed until {reopens}.</div>
                )}
                <button data-testid="submit-transfer-btn" onClick={create}
                  disabled={type === "TRANSFER" && windowClosed}
                  title={type === "TRANSFER" && windowClosed ? `Transfer window closed — reopens ${reopens}` : "Submit request"}
                  className="w-full sm:w-auto px-4 py-1.5 rounded-lg bg-[#E5B842] text-[#070707] text-xs font-bold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
                  Submit request
                </button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export function ClubTransfersPanel({ clubId }) {
  const [reqs, setReqs] = useState([]);
  const load = useCallback(() => {
    if (!clubId) return;
    api.get(`/transfers/club/${clubId}`).then(({ data }) => setReqs(data.requests)).catch(() => setReqs([]));
  }, [clubId]);
  useEffect(() => { load(); }, [load]);
  const decide = async (r, decision) => {
    try { await api.post(`/transfers/requests/${r.transfer_request_id}/club-decision`, { decision }); toast.success(`Request ${decision}d`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Shell title="Transfer Requests" icon={<ArrowLeftRight className="w-4 h-4" />} testid="club-transfers-panel">
      {reqs.length === 0 ? <div className="text-xs text-slate-500 font-mono">No transfer requests.</div> : (
        <div className="space-y-2">
          {reqs.map((r) => (
            <div key={r.transfer_request_id} data-testid={`club-transfer-${r.transfer_request_id}`} className="rounded-xl bg-[#070707] border border-[#2b2512] p-3">
              <StatusLine r={r} />
              {r.status === "AWAITING_CLUB_DECISION" && r.current_club_id === clubId && (
                <div className="flex gap-2 mt-2">
                  <button data-testid="club-approve-btn" onClick={() => decide(r, "approve")} className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200"><Check className="w-3.5 h-3.5" /> Approve</button>
                  <button data-testid="club-reject-btn" onClick={() => decide(r, "reject")} className="inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200"><X className="w-3.5 h-3.5" /> Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Shell>
  );
}

export function FederationTransfersPanel() {
  const [pending, setPending] = useState([]);
  const load = useCallback(() => {
    api.get("/transfers/federation/pending").then(({ data }) => setPending(data.pending)).catch(() => setPending([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const decide = async (r, decision) => {
    try { await api.post(`/transfers/requests/${r.transfer_request_id}/federation-decision`, { decision }); toast.success(`Federation ${decision}d`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const complete = async (r) => {
    try { await api.post(`/transfers/requests/${r.transfer_request_id}/complete`); toast.success("Transfer completed"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Shell title="Transfer Approvals" icon={<ShieldCheck className="w-4 h-4" />} testid="federation-transfers-panel">
      {pending.length === 0 ? <div className="text-xs text-slate-500 font-mono">No pending transfer approvals.</div> : (
        <div className="space-y-2">
          {pending.map((r) => (
            <div key={r.transfer_request_id} data-testid={`fed-transfer-${r.transfer_request_id}`} className="rounded-xl bg-[#070707] border border-[#2b2512] p-3">
              <StatusLine r={r} />
              <div className="flex gap-2 mt-2">
                <button data-testid="fed-approve-btn" onClick={() => decide(r, "approve")} className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200"><Check className="w-3.5 h-3.5" /> Approve</button>
                <button data-testid="fed-reject-btn" onClick={() => decide(r, "reject")} className="inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200"><X className="w-3.5 h-3.5" /> Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Shell>
  );
}
